import type {FC, ReactElement} from 'react'
import {useState} from 'react'
import {useUpdateLogbookEntryMutation} from '@apis/logbook'
import {useCustomTranslation} from '@hooks/useCustomTranslation'
import {useSnackbar} from '@hooks/useSnackbar'
import type {TenantLogbookEntriesResult} from '@interfaces/tenantLogbookEntries'
import BasicModal from '@shared/components/BasicModal'
import EntryComposer, {ComposerSaveButton} from '@shared/components/logbook/panel/EntryComposer'
import type {ComposerStatus, ComposerValues} from '@shared/components/logbook/panel/EntryComposer'

type LogbookEntry = TenantLogbookEntriesResult['results'][number]

const FORM_ID = 'logbook-edit-entry-form'

interface EditLogbookEntryModalProps {
  entry: LogbookEntry | null
  onClose: () => void
  onSaved: () => void
}

/** Edits one of the user's own entries from the Logbook page. */
const EditLogbookEntryModal: FC<EditLogbookEntryModalProps> = ({
  entry,
  onClose,
  onSaved,
}): ReactElement => {
  const [t] = useCustomTranslation('logbook')
  const [tCommon] = useCustomTranslation('common')
  const [showNotification] = useSnackbar()
  const [updateEntry] = useUpdateLogbookEntryMutation()
  const [status, setStatus] = useState<ComposerStatus>({canSave: false, saving: false})

  const save = async (values: ComposerValues): Promise<boolean> => {
    if (!entry) {
      return false
    }
    try {
      await updateEntry({
        entryType: entry.machine_sub_system ? 'equipment' : 'line',
        entryId: entry.id,
        body: {
          text: values.text,
          t_start: values.tStart,
          t_end: values.tEnd,
          category: values.categoryId,
        },
      }).unwrap()
      showNotification({message: t('entry_updated'), severity: 'success'})
      onSaved()
      onClose()
      return true
    } catch (error) {
      const data =
        error && typeof error === 'object' && 'data' in error
          ? (error.data as {detail?: string} | undefined)
          : undefined
      showNotification({
        message: data?.detail ?? tCommon('messages.unexpected_error'),
        severity: 'error',
      })
      return false
    }
  }

  return (
    <BasicModal
      open={!!entry}
      onClose={onClose}
      title={t('edit_entry')}
      maxWidth="520px"
      footerAction={
        <ComposerSaveButton
          formId={FORM_ID}
          status={status}
          label={tCommon('save')}
          dataCy="logbook-table-edit-save"
        />
      }
      dataCy="logbook-table-edit"
    >
      {entry ? (
        <EntryComposer
          key={entry.id}
          initialText={entry.text}
          initialCategory={entry.category}
          initialTime={{
            kind: 'custom',
            range: {
              start: new Date(entry.t_start).toISOString(),
              end: new Date(entry.t_end).toISOString(),
            },
          }}
          batchRange={null}
          placeholder={t('what_happened')}
          submitLabel={tCommon('save')}
          onSubmit={save}
          autoFocus
          dataCy="logbook-table-edit-entry"
          formId={FORM_ID}
          onStatusChange={setStatus}
        />
      ) : (
        <></>
      )}
    </BasicModal>
  )
}

export default EditLogbookEntryModal
